/**
 * Shipping promotions (S2S) — an operator manages its own shipping promotions from its backend: list, read, the
 * one that is live right now, create, update and disable. It is the console page's capability over the Operator
 * API, so it is signed exactly like the rest of paths.operator.ts (API key + HMAC), and its rules are the console's.
 */
const BASE = '/loot_machine/api/game_platform/v1';
const HOST = 'https://api.getcardbase.com';
const TAG = 'Shipping promotions (S2S)';

const KINDS = ['fixed_price', 'percent_off', 'amount_off'];
const FEES = ['shipping', 'insurance', 'signature', 'handling'];
const STATUSES = ['scheduled', 'active', 'ended', 'disabled'];

const security = () => [{ OperatorApiKey: [], RepackzTimestamp: [], RepackzSignature: [] }];

// --- The promotion ---------------------------------------------------------------------------------------------

const PROMOTION_SCHEMA = {
  type: 'object',
  title: 'ShippingPromotion',
  required: [
    'id',
    'label',
    'kind',
    'amount',
    'percent_off',
    'covers',
    'max_discount',
    'starts_at',
    'ends_at',
    'disabled_at',
    'status',
    'editable',
    'created_by',
    'updated_by',
    'created_at',
    'updated_at',
  ],
  properties: {
    id: { type: 'integer', description: 'Promotion id.', example: 12 },
    label: { type: 'string', maxLength: 80, description: 'The name players see. 1 to 80 characters.', example: 'November shipping promo' },
    kind: {
      type: 'string',
      enum: KINDS,
      description:
        'How the discount works. `fixed_price`: the covered parts together cost the player at most `amount` (0 means free); ' +
        'handling is charged first and covered shipping pays what is left. `percent_off`: that percentage off covered shipping ' +
        'and off handling. `amount_off`: `amount` off the covered parts, taken from handling first and then from covered ' +
        'shipping. You fund the discount.',
      example: 'fixed_price',
    },
    amount: {
      type: 'number',
      nullable: true,
      description:
        'In your currency units (`1.0`). The price for `fixed_price`, the sum taken off for `amount_off`; `null` for `percent_off`.',
      example: 1,
    },
    percent_off: {
      type: 'number',
      nullable: true,
      description: 'Above 0 and at most 100, two decimals. `null` unless `kind` is `percent_off`.',
      example: null,
    },
    covers: {
      type: 'array',
      minItems: 1,
      items: { type: 'string', enum: FEES },
      description:
        'The parts of the price it covers, at least one: `shipping`, `insurance`, `signature` and `handling`, listed in that ' +
        'order. `shipping` is the carrier rate alone (`shipping_fee_components.base` in the fee quote); `insurance` and ' +
        '`signature` are the add-ons; `handling` is the handling fee. A part it does not cover stays at its regular price, ' +
        'and the player pays it on top of the promotional price.',
      example: ['shipping', 'handling'],
    },
    max_discount: {
      type: 'number',
      nullable: true,
      description:
        'In your currency units, above 0: the most the promotion takes off one delivery. Over the cap the shipping part of ' +
        'the discount is cut first, then the handling part. `null` means no cap.',
      example: 25.5,
    },
    starts_at: {
      type: 'string',
      format: 'date-time',
      description: 'ISO 8601, UTC. The promotion is live while `starts_at <= now < ends_at`.',
      example: '2026-11-01T00:00:00Z',
    },
    ends_at: {
      type: 'string',
      format: 'date-time',
      description: 'ISO 8601, UTC. The instant itself is already outside the window.',
      example: '2026-12-01T00:00:00Z',
    },
    disabled_at: {
      type: 'string',
      format: 'date-time',
      nullable: true,
      description: 'When it was disabled (ISO 8601, UTC); `null` otherwise.',
      example: null,
    },
    status: {
      type: 'string',
      enum: STATUSES,
      description:
        '`scheduled` starts later, `active` is live now, `ended` has passed its `ends_at`, `disabled` was switched off ' +
        '(`disabled` wins over the other three).',
      example: 'scheduled',
    },
    editable: {
      type: 'boolean',
      description: 'True only for `scheduled` and `active`; an `ended` or `disabled` promotion cannot be edited.',
      example: true,
    },
    created_by: {
      type: 'string',
      nullable: true,
      description:
        'Who created it: `"api"` when it was created through this API, the e-mail of the member of your team who ' +
        'created it in the console, or `"Repackz support"` when our support team did. `null` when no author is recorded.',
      example: 'api',
    },
    updated_by: {
      type: 'string',
      nullable: true,
      description:
        'Who made the last change, the create included: `"api"` for a change through this API, the e-mail of the member ' +
        'of your team who changed it in the console, or `"Repackz support"` when our support team did. `null` when no ' +
        'change is recorded.',
      example: 'api',
    },
    created_at: { type: 'string', format: 'date-time', description: 'ISO 8601, UTC.', example: '2026-10-30T12:00:00Z' },
    updated_at: { type: 'string', format: 'date-time', description: 'ISO 8601, UTC.', example: '2026-10-30T12:00:00Z' },
  },
} as const;

const FIXED_PRICE_PROMOTION = {
  id: 12,
  label: 'November shipping promo',
  kind: 'fixed_price',
  amount: 1,
  percent_off: null,
  covers: ['shipping', 'handling'],
  max_discount: 25.5,
  starts_at: '2026-11-01T00:00:00Z',
  ends_at: '2026-12-01T00:00:00Z',
  disabled_at: null,
  status: 'scheduled',
  editable: true,
  created_by: 'api',
  updated_by: 'api',
  created_at: '2026-10-30T12:00:00Z',
  updated_at: '2026-10-30T12:00:00Z',
};

// Runs until the November promotion starts: the windows touch and do not overlap.
const WEEKEND_PROMOTION = {
  id: 13,
  label: 'Free shipping weekend',
  kind: 'percent_off',
  amount: null,
  percent_off: 100,
  covers: ['shipping'],
  max_discount: null,
  starts_at: '2026-10-30T12:00:00Z',
  ends_at: '2026-11-01T00:00:00Z',
  disabled_at: null,
  status: 'active',
  editable: true,
  created_by: 'api',
  updated_by: 'api',
  created_at: '2026-10-30T12:00:00Z',
  updated_at: '2026-10-30T12:00:00Z',
};

const UPDATED_PROMOTION = {
  ...FIXED_PRICE_PROMOTION,
  amount: 1.5,
  max_discount: null,
  ends_at: '2026-12-03T00:00:00Z',
  updated_at: '2026-10-30T12:05:00Z',
};

const DISABLED_PROMOTION = {
  ...UPDATED_PROMOTION,
  disabled_at: '2026-10-30T12:10:00Z',
  status: 'disabled',
  editable: false,
  updated_at: '2026-10-30T12:10:00Z',
};

const one = (promotion: unknown) => ({ result: { shipping_promotion: promotion } });

const oneResultSchema = (title: string) => ({
  type: 'object',
  title,
  required: ['result'],
  properties: {
    result: {
      type: 'object',
      required: ['shipping_promotion'],
      properties: { shipping_promotion: PROMOTION_SCHEMA },
    },
  },
});

// --- What you send ---------------------------------------------------------------------------------------------

const INPUT_FIELDS = {
  label: {
    type: 'string',
    minLength: 1,
    maxLength: 80,
    description: 'The name players see. 1 to 80 characters.',
    example: 'November shipping promo',
  },
  kind: {
    type: 'string',
    enum: KINDS,
    default: 'fixed_price',
    description: 'How the discount works (see `kind` on the promotion). Defaults to `fixed_price`; send it anyway.',
    example: 'fixed_price',
  },
  covers: {
    type: 'array',
    minItems: 1,
    items: { type: 'string', enum: FEES },
    description:
      'The parts of the price it covers, at least one, in any order: `shipping` (the carrier rate alone), `insurance`, ' +
      '`signature` and `handling`. Required on a create: there is no default. An update replaces the whole list. A part it ' +
      'does not cover stays at its regular price, and the player pays it on top of the promotional price.',
    example: ['shipping', 'handling'],
  },
  amount: {
    type: 'number',
    minimum: 0,
    description:
      'In your currency units. Required for `fixed_price` (0 or more; 0 makes the covered fees free) and `amount_off` ' +
      '(more than 0). Cleared for `percent_off`.',
    example: 1,
  },
  percent_off: {
    type: 'number',
    exclusiveMinimum: 0,
    maximum: 100,
    description: 'Above 0 and at most 100. Required for `percent_off`; cleared for the other kinds.',
    example: 100,
  },
  max_discount: {
    type: 'number',
    exclusiveMinimum: 0,
    nullable: true,
    description:
      'In your currency units, above 0: the most the promotion takes off one delivery. Left out or `null` for no cap.',
    example: 25.5,
  },
  starts_at: {
    type: 'string',
    format: 'date-time',
    description: 'ISO 8601; no offset means UTC. The promotion is live from this instant.',
    example: '2026-11-01T00:00:00Z',
  },
  ends_at: {
    type: 'string',
    format: 'date-time',
    description: 'ISO 8601; no offset means UTC. Must be after `starts_at`. The instant itself is already outside the window.',
    example: '2026-12-01T00:00:00Z',
  },
};

const CREATE_REQUEST_SCHEMA = {
  type: 'object',
  title: 'CreateShippingPromotionRequest',
  required: ['shipping_promotion'],
  properties: {
    shipping_promotion: {
      type: 'object',
      required: ['label', 'covers', 'starts_at', 'ends_at'],
      description:
        'The promotion. Besides these four, send the rule value of its kind: `amount` for `fixed_price` and `amount_off`, ' +
        '`percent_off` for `percent_off`. Other keys are ignored.',
      properties: INPUT_FIELDS,
    },
  },
};

const UPDATE_REQUEST_SCHEMA = {
  type: 'object',
  title: 'UpdateShippingPromotionRequest',
  required: ['shipping_promotion'],
  properties: {
    shipping_promotion: {
      type: 'object',
      minProperties: 1,
      description: 'Only the keys you send change. `null` clears `max_discount`. `covers` replaces the whole list. Other keys are ignored.',
      properties: INPUT_FIELDS,
    },
  },
};

const CREATE_FIXED_PRICE = {
  shipping_promotion: {
    label: 'November shipping promo',
    kind: 'fixed_price',
    amount: 1,
    covers: ['shipping', 'handling'],
    max_discount: 25.5,
    starts_at: '2026-11-01T00:00:00Z',
    ends_at: '2026-12-01T00:00:00Z',
  },
};

const CREATE_PERCENT_OFF = {
  shipping_promotion: {
    label: 'Free shipping weekend',
    kind: 'percent_off',
    percent_off: 100,
    covers: ['shipping'],
    starts_at: '2026-10-30T12:00:00Z',
    ends_at: '2026-11-01T00:00:00Z',
  },
};

const UPDATE_PARTIAL = {
  shipping_promotion: { amount: 1.5, max_discount: null, ends_at: '2026-12-03T00:00:00Z' },
};

// --- What it answers -------------------------------------------------------------------------------------------

// The ad-hoc codes of this feature carry no numeric id, and a validation failure lists its problems in `errors`.
const PROMOTION_ERROR_SCHEMA = {
  type: 'object',
  title: 'ShippingPromotionError',
  required: ['error', 'error_detail'],
  properties: {
    error: { type: 'integer', enum: [1], description: 'Non-zero marks a failure.' },
    error_detail: {
      type: 'object',
      required: ['code', 'message'],
      properties: {
        id: {
          type: 'integer',
          description: 'Numeric error id. Only the catalog codes carry one; the promotion codes below do not.',
        },
        code: { type: 'string', description: 'Machine-readable error code — match on this, not on `message`.' },
        message: { type: 'string', description: 'Human-readable text. For `VALIDATION_FAILED`, the problems joined with `; `.' },
        errors: {
          type: 'array',
          items: { type: 'string' },
          description: 'Only on `VALIDATION_FAILED`: every problem found, one string each.',
        },
      },
    },
  },
};

const envelope = () => ({ $ref: '#/components/schemas/ErrorEnvelope' });

const failure = (code: string, message: string, id?: number) => ({
  error: 1,
  error_detail: { ...(id === undefined ? {} : { id }), code, message },
});

const validation = (...errors: string[]) => ({
  error: 1,
  error_detail: { code: 'VALIDATION_FAILED', message: errors.join('; '), errors },
});

const errorResponse = (description: string, schema: unknown, examples?: Record<string, unknown>) => ({
  description,
  content: { 'application/json': { schema, ...(examples ? { examples } : {}) } },
});

const unauthorized = () =>
  errorResponse(
    'Authentication failed — `INVALID_API_KEY`, `INVALID_SIGNATURE` (a wrong signature, or a missing timestamp or ' +
      'signature header), or `EXPIRED_TIMESTAMP` (timestamp more than 5 minutes off).',
    envelope(),
  );

const forbidden = () =>
  errorResponse(
    '`SHIPPING_PROMOTIONS_DISABLED` — promotions are not enabled for your account yet. `IP_NOT_WHITELISTED` (id 1002) — ' +
      'the request came from an IP not on your allow-list. Authentication and the IP allow-list are checked before the ' +
      'promotions gate.',
    PROMOTION_ERROR_SCHEMA,
    {
      not_enabled: {
        summary: 'Promotions are not enabled for your account',
        value: failure('SHIPPING_PROMOTIONS_DISABLED', 'Shipping promotions are not enabled for this operator.'),
      },
    },
  );

const notFound = () =>
  errorResponse(
    '`NOT_FOUND` (id 4040) — no promotion with that id belongs to your account. An unknown id, a non-numeric id and ' +
      'another operator’s promotion all answer the same.',
    envelope(),
    { unknown: { summary: 'Unknown id', value: failure('NOT_FOUND', 'Resource not found', 4040) } },
  );

const internal = () =>
  errorResponse('`INTERNAL_ERROR` — unexpected error. The body includes a correlation id for support.', envelope());

const OVERLAP = validation('Overlaps another shipping promotion');
const BODY_REQUIRED = failure('SHIPPING_PROMOTION_REQUIRED', 'shipping_promotion is required and must be an object.');

const ID_PARAMETER = {
  name: 'id',
  in: 'path',
  required: true,
  schema: { type: 'integer' },
  description: 'Promotion id, e.g. from the list. It must be one of your promotions.',
};

// --- Signed code samples ---------------------------------------------------------------------------------------

type Method = 'GET' | 'POST' | 'PATCH';

// A Ruby literal for a JSON value: { key: 'text' }, nil for null.
function ruby(value: unknown): string {
  if (value === null) return 'nil';
  if (typeof value === 'string') return `'${value.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`;
  if (Array.isArray(value)) return `[${value.map(ruby).join(', ')}]`;
  if (typeof value === 'object') {
    return `{ ${Object.entries(value as Record<string, unknown>).map(([key, item]) => `${key}: ${ruby(item)}`).join(', ')} }`;
  }
  return String(value);
}

/**
 * The Ruby / Node.js / cURL samples of one signed request. A request with a body sends it as JSON with the JSON
 * header — curl's `-d` alone would send a form encoding, and the body would not be read.
 */
function samples(method: Method, path: string, options: { body?: Record<string, unknown>; note?: string } = {}) {
  const { body, note } = options;
  const verb = `${method.charAt(0)}${method.slice(1).toLowerCase()}`;
  const empty = method === 'GET' ? 'GET has an empty body' : 'POST with no body';
  const json = body ? JSON.stringify(body) : '';
  const rubyArgs = body ? Object.entries(body).map(([key, item]) => `${key}: ${ruby(item)}`).join(', ') : '';

  return [
    {
      lang: 'ruby',
      label: 'Ruby',
      source: [
        "require 'openssl'; require 'net/http'; require 'json'; require 'uri'",
        "api_key, secret = ENV['REPACKZ_API_KEY'], ENV['REPACKZ_API_SECRET']",
        ...(note ? [`# ${note}`] : []),
        `path = '${path}'`,
        body ? `body = JSON.generate(${rubyArgs})` : `body = '' # ${empty}`,
        'ts = Time.now.to_i.to_s',
        `signing_string = "#{ts}.${method} #{path}.#{body}"`,
        "sig = 'sha256=' + OpenSSL::HMAC.hexdigest('SHA256', secret, signing_string)",
        `uri = URI("${HOST}#{path}")`,
        `req = Net::HTTP::${verb}.new(uri)`,
        'req[\'Authorization\'] = "Bearer #{api_key}"',
        "req['X-Repackz-Timestamp'] = ts",
        "req['X-Repackz-Signature'] = sig",
        ...(body ? ["req['Content-Type'] = 'application/json'", 'req.body = body'] : []),
        'res = Net::HTTP.start(uri.host, uri.port, use_ssl: true) { |h| h.request(req) }',
        'puts res.body',
      ].join('\n'),
    },
    {
      lang: 'node',
      label: 'Node.js',
      source: [
        "const crypto = require('crypto');",
        'const apiKey = process.env.REPACKZ_API_KEY, secret = process.env.REPACKZ_API_SECRET;',
        ...(note ? [`// ${note}`] : []),
        `const path = '${path}';`,
        body ? `const body = JSON.stringify(${json});` : `const body = ''; // ${empty}`,
        'const ts = Math.floor(Date.now() / 1000).toString();',
        `const signingString = \`\${ts}.${method} \${path}.\${body}\`;`,
        "const sig = 'sha256=' + crypto.createHmac('sha256', secret).update(signingString).digest('hex');",
        `const res = await fetch(\`${HOST}\${path}\`, {`,
        `  method: '${method}',`,
        '  headers: {',
        "    'Authorization': `Bearer ${apiKey}`,",
        "    'X-Repackz-Timestamp': ts,",
        "    'X-Repackz-Signature': sig,",
        ...(body ? ["    'Content-Type': 'application/json',"] : []),
        '  },',
        ...(body ? ['  body,'] : []),
        '});',
        'console.log(await res.json());',
      ].join('\n'),
    },
    {
      lang: 'shell',
      label: 'cURL',
      source: [
        'API_KEY="$REPACKZ_API_KEY"; SECRET="$REPACKZ_API_SECRET"',
        ...(note ? [`# ${note}`] : []),
        `PATH_URI="${path}"`,
        body ? `BODY='${json}'` : `BODY='' # ${empty}`,
        'TS=$(date +%s)',
        `SIGNING_STRING="$TS.${method} $PATH_URI.$BODY"`,
        'SIG="sha256=$(printf \'%s\' "$SIGNING_STRING" | openssl dgst -sha256 -hmac "$SECRET" | sed \'s/^.* //\')"',
        `curl -sS${method === 'GET' ? '' : ` -X ${method}`} "${HOST}$PATH_URI" \\`,
        '  -H "Authorization: Bearer $API_KEY" \\',
        '  -H "X-Repackz-Timestamp: $TS" \\',
        body ? '  -H "X-Repackz-Signature: $SIG" \\' : '  -H "X-Repackz-Signature: $SIG"',
        ...(body ? ["  -H 'Content-Type: application/json' \\", '  -d "$BODY"'] : []),
      ].join('\n'),
    },
  ];
}

// --- The six operations ----------------------------------------------------------------------------------------

const VALIDATION_MESSAGES = [
  "Label can't be blank",
  'Label is too long (maximum is 80 characters)',
  'Kind is not included in the list',
  'Covers must include at least one fee',
  'Covers has unknown fees: x',
  'Covers must be a list of fees',
  'Amount is not a number',
  'Amount is too large',
  'Amount must be greater than or equal to 0',
  'Amount must be greater than 0',
  'Max discount is not a number',
  'Max discount is too large',
  'Max discount must be greater than 0',
  'Percent off is not a number',
  'Percent off must be greater than 0',
  'Percent off must be less than or equal to 100',
  'Starts at is invalid',
  'Ends at is invalid',
  "Starts at can't be blank",
  "Ends at can't be blank",
  'Ends at must be after the start',
  'Overlaps another shipping promotion',
  'Ended or disabled promotions cannot be edited',
];

const JSON_HEADER =
  'Send it with `Content-Type: application/json` — without it the body is not read and the call answers ' +
  '`422 SHIPPING_PROMOTION_REQUIRED`.';

export const shippingPromotionsPaths: Record<string, unknown> = {
  [`${BASE}/shipping_promotions`]: {
    get: {
      tags: [TAG],
      summary: 'List your shipping promotions',
      description:
        'Your shipping promotions, newest `starts_at` first (ties by id, newest first) — the list the console’s ' +
        '**Shipping Promotions** page shows. Only your own promotions are ever returned. `status` narrows it to one of ' +
        'the four states; `total_count` is the count after the `status` filter.',
      operationId: 'operatorListShippingPromotions',
      security: security(),
      parameters: [
        {
          name: 'status',
          in: 'query',
          required: false,
          schema: { type: 'string', enum: STATUSES },
          description: 'Only promotions in this state. Anything else is refused with `422 INVALID_STATUS`.',
        },
        {
          name: 'per',
          in: 'query',
          required: false,
          schema: { type: 'integer', default: 20 },
          description: 'Page size. Defaults to 20 (also for a missing or non-positive value) and is capped at 200.',
        },
        {
          name: 'page',
          in: 'query',
          required: false,
          schema: { type: 'integer', minimum: 1, default: 1 },
          description: '1-based page number.',
        },
      ],
      responses: {
        '200': {
          description: 'A page of your promotions, newest `starts_at` first.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'ShippingPromotionsResponse',
                required: ['result'],
                properties: {
                  result: {
                    type: 'object',
                    required: ['shipping_promotions', 'meta'],
                    properties: {
                      shipping_promotions: { type: 'array', items: PROMOTION_SCHEMA },
                      meta: {
                        type: 'object',
                        title: 'ShippingPromotionsMeta',
                        required: ['page', 'per', 'total_count'],
                        properties: {
                          page: { type: 'integer', description: 'Current page (1-based).' },
                          per: { type: 'integer', description: 'Page size used.' },
                          total_count: { type: 'integer', description: 'Promotions across all pages, after the `status` filter.' },
                        },
                      },
                    },
                  },
                },
              },
              examples: {
                all: {
                  summary: 'No filter',
                  value: {
                    result: {
                      shipping_promotions: [FIXED_PRICE_PROMOTION, WEEKEND_PROMOTION],
                      meta: { page: 1, per: 20, total_count: 2 },
                    },
                  },
                },
                active_only: {
                  summary: '`?status=active&per=1`',
                  value: {
                    result: { shipping_promotions: [WEEKEND_PROMOTION], meta: { page: 1, per: 1, total_count: 1 } },
                  },
                },
              },
            },
          },
        },
        '401': unauthorized(),
        '403': forbidden(),
        '422': errorResponse(
          '`INVALID_STATUS` (id 2094) — `status` is not one of `scheduled`, `active`, `ended`, `disabled`.',
          envelope(),
          { invalid_status: { summary: 'Unknown status filter', value: failure('INVALID_STATUS', 'Invalid status filter', 2094) } },
        ),
        '500': internal(),
      },
      'x-codeSamples': samples('GET', `${BASE}/shipping_promotions?status=active&per=20&page=1`, {
        note: 'Filters go into the query string — it is part of the signed path.',
      }),
    },

    post: {
      tags: [TAG],
      summary: 'Create a shipping promotion',
      description: [
        'Create a promotion. The call answers `200` with the promotion (not `201`): `scheduled` until its window opens, ' +
          'or `active` at once if it has already begun. How a promotion prices a delivery — the types, the coverage, the ' +
          'cap — is explained in the overview under **Shipping promotions**.',
        'The body is a JSON object under `shipping_promotion`. ' +
          JSON_HEADER +
          ' Known keys: `label`, `kind`, `covers`, `amount`, `max_discount`, `percent_off`, `starts_at`, `ends_at`; ' +
          'any other key is ignored, and the author is never read from the body.',
        'Required: `label`, `covers`, `starts_at`, `ends_at`, and the rule value of the kind — `amount` for `fixed_price` (0 or more) ' +
          'and `amount_off` (more than 0), `percent_off` for `percent_off`. Send `kind` anyway: it defaults to `fixed_price` ' +
          'when left out. `covers` has no default: it lists the parts of the price the promotion lowers, and what it leaves out ' +
          'stays at its regular price. The rule value of the other kind is cleared — a `percent_off` promotion stores no amount, ' +
          'the others no percentage.',
        'Numbers (`amount`, `max_discount`, `percent_off`) are JSON numbers or strings of plain decimal digits (`1`, `1.5`, ' +
          '`"1.50"`) — no exponents, no comma decimals, no `NaN`; money is in your currency units. Times are whole ISO 8601 ' +
          'date-times: `YYYY-MM-DDTHH:MM`, optional seconds with a fraction, optional `Z` or `+HH:MM` / `-HH:MM`; no offset ' +
          'means UTC; a date alone (`2026-11-01`) is refused; years 2000 to 2100; `ends_at` must be after `starts_at`.',
        'Windows are half-open (`starts_at <= t < ends_at`), so a promotion may start exactly when the previous one ends. ' +
          'Two of your promotions may not overlap, except that a disabled promotion blocks nothing. A start in the past is ' +
          'not refused: a window that has already begun is `active` at once, and one wholly in the past is created `ended`.',
        '**There is no idempotency key.** A create you repeat — after a timeout, say — is refused with ' +
          '`422 VALIDATION_FAILED` (*Overlaps another shipping promotion*) and makes no second promotion. That refusal ' +
          'looks the same as a real conflict with another promotion, so after a timeout read the promotion back before you ' +
          'treat it as a failure: `GET /shipping_promotions?status=scheduled` for one that starts later, or the list. ' +
          '`GET /shipping_promotions/active` shows only a promotion whose window has already started.',
        '**Validation.** A refused body answers `422 VALIDATION_FAILED` and lists every problem in `error_detail.errors` ' +
          '(`message` is the same list joined with `; `). The messages are: ' +
          VALIDATION_MESSAGES.map((message) => `\`${message}\``).join(', ') +
          '. The last one can only come back from an update. Match on `code`, not on the text.',
      ].join('\n\n'),
      operationId: 'operatorCreateShippingPromotion',
      security: security(),
      requestBody: {
        required: true,
        content: {
          'application/json': {
            schema: CREATE_REQUEST_SCHEMA,
            examples: {
              fixed_price: { summary: 'A $1 price for shipping + handling, capped at $25.50', value: CREATE_FIXED_PRICE },
              percent_off: { summary: 'Free shipping for a weekend', value: CREATE_PERCENT_OFF },
            },
          },
        },
      },
      responses: {
        '200': {
          description: 'The promotion as created.',
          content: {
            'application/json': {
              schema: oneResultSchema('CreateShippingPromotionResponse'),
              examples: {
                fixed_price: { summary: 'The $1 price', value: one(FIXED_PRICE_PROMOTION) },
                percent_off: { summary: 'The weekend promotion', value: one(WEEKEND_PROMOTION) },
              },
            },
          },
        },
        '401': unauthorized(),
        '403': forbidden(),
        '422': errorResponse(
          '`VALIDATION_FAILED` — a value was refused or the window overlaps another promotion; `error_detail.errors` lists ' +
            'every problem. `SHIPPING_PROMOTION_REQUIRED` — `shipping_promotion` is missing, `null`, not an object, `{}`, or ' +
            'an object with none of the known keys; it is also what a body sent without `Content-Type: application/json` ' +
            'gets, because it is then not read. Nothing is created in either case.',
          PROMOTION_ERROR_SCHEMA,
          {
            overlap: { summary: 'The window overlaps another promotion — also what a retried create gets', value: OVERLAP },
            several: {
              summary: 'Several problems at once',
              value: validation('Kind is not included in the list', 'Amount is not a number', 'Starts at is invalid'),
            },
            missing_fields: {
              summary: 'A create that forgot the required fields',
              value: validation(
                "Label can't be blank",
                'Amount is not a number',
                "Starts at can't be blank",
                "Ends at can't be blank",
                'Covers must include at least one fee',
              ),
            },
            window: { summary: 'The window ends before it starts', value: validation('Ends at must be after the start') },
            percent_range: { summary: 'A percentage out of range', value: validation('Percent off must be greater than 0') },
            body_required: { summary: 'No shipping_promotion object', value: BODY_REQUIRED },
          },
        ),
        '500': internal(),
      },
      'x-codeSamples': samples('POST', `${BASE}/shipping_promotions`, { body: CREATE_FIXED_PRICE }),
    },
  },

  [`${BASE}/shipping_promotions/active`]: {
    get: {
      tags: [TAG],
      summary: 'Get the live shipping promotion',
      description:
        'The promotion that is live right now — the one that prices a delivery created this instant — or `null` when none ' +
        'is. It shows only a promotion whose window has already started: one that starts later is not here until it opens, ' +
        'so read those with `GET /shipping_promotions?status=scheduled`.',
      operationId: 'operatorGetActiveShippingPromotion',
      security: security(),
      responses: {
        '200': {
          description: 'The live promotion, or `shipping_promotion: null` when none is.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'ActiveShippingPromotionResponse',
                required: ['result'],
                properties: {
                  result: {
                    type: 'object',
                    required: ['shipping_promotion'],
                    properties: { shipping_promotion: { ...PROMOTION_SCHEMA, nullable: true } },
                  },
                },
              },
              examples: {
                live: { summary: 'One is live', value: one(WEEKEND_PROMOTION) },
                none: { summary: 'Nothing is live', value: one(null) },
              },
            },
          },
        },
        '401': unauthorized(),
        '403': forbidden(),
        '500': internal(),
      },
      'x-codeSamples': samples('GET', `${BASE}/shipping_promotions/active`),
    },
  },

  [`${BASE}/shipping_promotions/{id}`]: {
    get: {
      tags: [TAG],
      summary: 'Get a shipping promotion',
      description:
        'One of your promotions by id. An id that does not exist, is not a number or belongs to another operator is the ' +
        'same `404`.',
      operationId: 'operatorGetShippingPromotion',
      security: security(),
      parameters: [ID_PARAMETER],
      responses: {
        '200': {
          description: 'The promotion.',
          content: {
            'application/json': {
              schema: oneResultSchema('ShippingPromotionResponse'),
              examples: { promotion: { summary: 'A scheduled promotion', value: one(FIXED_PRICE_PROMOTION) } },
            },
          },
        },
        '401': unauthorized(),
        '403': forbidden(),
        '404': notFound(),
        '500': internal(),
      },
      'x-codeSamples': samples('GET', `${BASE}/shipping_promotions/12`),
    },

    patch: {
      tags: [TAG],
      summary: 'Update a shipping promotion',
      description: [
        'Change a promotion. Only the keys you send change; the rest keep their value. `null` (or an empty string) clears ' +
          '`max_discount`; `null` for a required value is a validation error. An update can change the kind. `covers` replaces ' +
          'the whole list, so send every part the promotion should cover. A change applies to deliveries created after the ' +
          'call; deliveries already created keep the price their players paid.',
        'An `ended` or `disabled` promotion cannot be edited — this is checked first, so an edit cannot reopen an ended ' +
          'promotion by moving `ends_at` — and answers `422 SHIPPING_PROMOTION_NOT_EDITABLE`. (If the promotion ends between ' +
          'that check and the save, the same text comes back as a `VALIDATION_FAILED` message.)',
        'The body is a JSON object under `shipping_promotion`, with the same keys, formats and window rules as a create. ' +
          JSON_HEADER +
          ' A `PATCH` with the same body leaves the same state, so it is safe to repeat. A refused body answers ' +
          '`422 VALIDATION_FAILED` with the problems in `error_detail.errors`, as for a create.',
      ].join('\n\n'),
      operationId: 'operatorUpdateShippingPromotion',
      security: security(),
      parameters: [ID_PARAMETER],
      requestBody: {
        required: true,
        content: {
          'application/json': {
            schema: UPDATE_REQUEST_SCHEMA,
            examples: { partial: { summary: 'Raise the price, drop the cap and extend the end', value: UPDATE_PARTIAL } },
          },
        },
      },
      responses: {
        '200': {
          description: 'The promotion after the change.',
          content: {
            'application/json': {
              schema: oneResultSchema('UpdateShippingPromotionResponse'),
              examples: { partial: { summary: 'Only amount, max_discount and ends_at changed', value: one(UPDATED_PROMOTION) } },
            },
          },
        },
        '401': unauthorized(),
        '403': forbidden(),
        '404': notFound(),
        '422': errorResponse(
          '`SHIPPING_PROMOTION_NOT_EDITABLE` — the promotion is `ended` or `disabled`. `VALIDATION_FAILED` — a value was ' +
            'refused or the new window overlaps another promotion; `error_detail.errors` lists every problem. ' +
            '`SHIPPING_PROMOTION_REQUIRED` — `shipping_promotion` is missing, `null`, not an object, `{}`, or an object with ' +
            'none of the known keys, or the body was sent without `Content-Type: application/json`. Nothing changes in ' +
            'any of these cases.',
          PROMOTION_ERROR_SCHEMA,
          {
            not_editable: {
              summary: 'The promotion is ended or disabled',
              value: failure('SHIPPING_PROMOTION_NOT_EDITABLE', 'Ended or disabled promotions cannot be edited'),
            },
            overlap: { summary: 'The new window overlaps another promotion', value: OVERLAP },
            body_required: { summary: 'No shipping_promotion object', value: BODY_REQUIRED },
          },
        ),
        '500': internal(),
      },
      'x-codeSamples': samples('PATCH', `${BASE}/shipping_promotions/12`, { body: UPDATE_PARTIAL }),
    },
  },

  [`${BASE}/shipping_promotions/{id}/disable`]: {
    post: {
      tags: [TAG],
      summary: 'Disable a shipping promotion',
      description:
        'Switch a `scheduled` or `active` promotion off now: deliveries created from this moment are priced at the regular ' +
        'fees, deliveries already created keep their price, and its window is freed at once. It cannot be undone — to run ' +
        'it again, create a new promotion. No request body.\n\n' +
        'Idempotent: on an `ended` or already `disabled` promotion it answers 200 with the promotion unchanged.',
      operationId: 'operatorDisableShippingPromotion',
      security: security(),
      parameters: [ID_PARAMETER],
      responses: {
        '200': {
          description: 'The promotion, now `disabled` — or unchanged if it was already `ended` or `disabled`.',
          content: {
            'application/json': {
              schema: oneResultSchema('DisableShippingPromotionResponse'),
              examples: { disabled: { summary: 'Disabled', value: one(DISABLED_PROMOTION) } },
            },
          },
        },
        '401': unauthorized(),
        '403': forbidden(),
        '404': notFound(),
        '500': internal(),
      },
      'x-codeSamples': samples('POST', `${BASE}/shipping_promotions/12/disable`),
    },
  },
};
